import {
  EXTENSIONS_SELF_REPORTED,
  EXTENSION_URL,
} from '@constants/extension-codes';

import { AllergyRecord, NO_KNOWN_ALLERGY_MAP } from '@models/allergy';
import type { AllergyValues } from '@models/allergy';
import {
  getAllergyType,
  getClinicalStatus,
  ALLERGY_SEVERITY_MAP,
} from '@models/allergy';

import { capitalize, formatPatientId } from '@utils/format';

import type { AllergyModelInput, Extension } from 'generated/types';

export function getAllergyTemplate(
  patient_id: string,
  values: AllergyValues
): AllergyModelInput {
  const extension: Extension[] = [];
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extension.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }
  if (values.selfReported) {
    extension.push({
      url: `${EXTENSION_URL}${EXTENSIONS_SELF_REPORTED}`,
      valueBoolean: values.selfReported,
    });
  }

  return {
    resourceType: 'AllergyIntolerance',
    patient: {
      reference: formatPatientId(patient_id),
    },
    clinicalStatus: {
      text: capitalize(values.clinicalStatus ?? ''),
      coding: [
        {
          code: getClinicalStatus(values.clinicalStatus ?? 'active'),
          display: capitalize(values.clinicalStatus ?? ''),
          system:
            'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
        },
      ],
    },
    code: {
      text: values.noAllergies
        ? NO_KNOWN_ALLERGY_MAP[values.noAllergies]
        : values.title,
      ...(values.noAllergies && {
        coding: [
          {
            code: values.noAllergies,
            display: NO_KNOWN_ALLERGY_MAP[values.noAllergies],
            system: AllergyRecord.VITALL_SYSTEM,
            userSelected: true,
          },
        ],
      }),
    },
    ...(values.lastKnownReaction && {
      lastOccurrence: values.lastKnownReaction,
    }),
    ...(values.allergyType && {
      category: [getAllergyType(values.allergyType)],
    }),
    ...((values.reactionDescription ||
      values.severity ||
      values.title ||
      values.responseDescription) && {
      reaction: [
        {
          ...(values.reactionDescription && {
            description: values.reactionDescription,
          }),
          manifestation: [
            {
              text: values.severity
                ? ALLERGY_SEVERITY_MAP[values.severity]
                : 'Unknown',
            },
          ],
          ...(values.responseDescription && {
            note: [
              {
                text: values.responseDescription,
              },
            ],
          }),
          ...(values.severity && {
            severity: values.severity,
          }),
          ...(values.title && {
            substance: {
              text: values.title,
            },
          }),
        },
      ],
    }),
    ...(extension.length && {
      extension,
    }),
  };
}
